import {
  Coffee,
  Email,
  FavoriteRounded,
  GitHub,
  Instagram,
  LinkedIn,
  LocationOn,
  Phone
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import me from '../../constants/me';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, rgba(34, 40, 49, 0.95) 100%)`,
  borderTop: '1px solid rgba(0, 173, 181, 0.1)',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  background: 'rgba(0, 173, 181, 0.1)',
  color: theme.palette.primary.main,
  border: '1px solid rgba(0, 173, 181, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: theme.palette.primary.main,
    color: theme.palette.background.default,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 173, 181, 0.4)',
  },
}));

const IconButtonWithLabel = styled(Button)(({ theme }) => ({
  background: 'rgba(0, 173, 181, 0.1)',
  color: theme.palette.primary.main,
  border: '1px solid rgba(0, 173, 181, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: theme.palette.primary.main,
    color: theme.palette.background.default,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 173, 181, 0.4)',
  },
  
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
  },
  '& svg': {
    color: theme.palette.primary.main,
    fontSize: '1.2rem',
  },
}));

const Footer: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: <GitHub />, url: 'https://github.com/tony3898', label: 'GitHub' },
    { icon: <LinkedIn />, url: 'https://www.linkedin.com/in/tejasrana3898/', label: 'LinkedIn' },
    { icon: <Instagram />, url: 'https://www.instagram.com/tony_3898/', label: 'Instagram' },
    { icon: <Coffee />, url: 'https://www.buymeacoffee.com/tony_3898', label: 'Buy Me a Coffee', showLabel: true },
  ];

  const contactInfo = [
    { icon: <Email />, text: me.email, href: `mailto:${me.email}` },
    { icon: <Phone />, text: me.phone, href: `tel:${me.phone}` },
    { icon: <LocationOn />, text: 'Mumbai, India', href: null },
  ];

  return (
    <FooterContainer id="contact">
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Gilroy, sans-serif',
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Let's Connect
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 3,
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            I'm always interested in hearing about new projects and opportunities.
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
            {socialLinks.map((social, index) => {
              if(social.showLabel){
                return (
                  <IconButtonWithLabel
                    key={index}
                    onClick={() => window.open(social.url, '_blank')}
                    aria-label={social.label}
                    startIcon={social.icon}
                    aria-labelledby={social.label}
                  >
                    {social.label}
                  </IconButtonWithLabel>
                )
              }
              return (
                <SocialIconButton
                  key={index}
                  onClick={() => window.open(social.url, '_blank')}
                  aria-label={social.label}
                >
                  {social.icon}
                </SocialIconButton>
              )
            })}
          </Stack>
        </Box>

        <Divider sx={{ mb: 4, borderColor: 'rgba(0, 173, 181, 0.1)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'flex-start' },
            gap: 4,
          }}
        >
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Gilroy, sans-serif',
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {me.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                maxWidth: 300,
                lineHeight: 1.6,
              }}
            >
              Full Stack Developer passionate about creating innovative digital solutions
              that make a positive impact.
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              Contact Info
            </Typography>
            {contactInfo.map((contact, index) => (
              <ContactItem key={index}>
                {contact.icon}
                {contact.href ? (
                  <Link
                    href={contact.href}
                    color="inherit"
                    underline="hover"
                    sx={{ fontSize: '0.9rem' }}
                  >
                    {contact.text}
                  </Link>
                ) : (
                  <Typography variant="body2">{contact.text}</Typography>
                )}
              </ContactItem>
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 4, borderColor: 'rgba(0, 173, 181, 0.1)' }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            © {currentYear} {me.name}. Made with
            <FavoriteRounded sx={{ color: 'primary.main', fontSize: '1rem' }} />
          </Typography>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 